import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  AlertTriangle, Banknote, CalendarCog, CheckCircle2, CircleDollarSign, Gift, Landmark, ListChecks, MinusCircle, RefreshCw, ShieldCheck, Users, Wallet,
} from 'lucide-react'
import { Chip, ErrorBox, Kpi, Loading, n0, naira, nairaK, PALETTE, Panel, PyHeader, usePyData, usePyMeta, usePyPeriod } from '../py'
import { pyTo } from '../links'

// Stephanet's Payroll Navigator (custom/pay15/Dashboard.jsx), ported unchanged; links go to this app's routes (attendance is in HR & People).
const to = k => pyTo(k) || '#'

const NAV = [
  ['1 · Set-up (change rarely)', [['py-date-control', 'Date Control', 'Payroll month, cut-off day, work week, public holidays'],
    ['py-setup', 'Setup Rules', 'Rates, PAYE bands, salary split, absence profiles, OT rules'], ['py-projects', 'Projects', 'Projects/sites, status, site/hazard/outstation allowances'],
    ['py-grades', 'Salary Grades', 'Grade amounts, components, entitlement flags'], ['py-grading', 'Detailed Grading', 'Full grade ladder with every step coded'],
    ['py-benefits-setup', 'Benefits Setup', 'Allowances: amount, frequency, who is entitled'], ['py-employees', 'Employees', 'Staff records, pay structure, bank & pension details'],
    ['py-enrolment', 'Benefit Enrolment', 'Per-person benefit exceptions (Y / N)'], ['py-guide', 'Guide', 'How everything works, with a live calculator']]],
  ['2 · Attendance (every month — HR & People › Attendance Management)', [['py-att-import', 'Attendance Import', 'Paste any attendance report — the sites fill themselves'],
    ['py-att-manual', 'Attendance Manual', 'Corrections and sites without the device'], ['py-site-sheets', 'Site Sheets', 'One sheet per project'],
    ['py-att-check', 'Attendance Check', 'Same person on two sites on the same day'], ['py-att-grid', 'Attendance Grid', 'Where each person was each day']]],
  ['3 · Monthly inputs', [['py-deductions', 'Monthly Deductions', 'Leave days, advances, losses, penalties, additions'],
    ['py-loans', 'Loan Schedule', 'Loans with automatic monthly instalments'], ['py-savings', 'Basic Savings Deduction', 'Security withheld from Basic, losses charged against it'],
    ['py-other-exp', 'Other Expenses', 'Staff costs paid outside salary']]],
  ['4 · Reports', [['py-master', 'Master Summary', 'Everyone, all pay structures combined'], ['py-basic-pay', 'Basic Pay', 'Monthly staff (Net, Gross, Fixed)'],
    ['py-daily-pay', 'Daily Pay', 'Daily-rate staff'], ['py-financial', 'Financial', 'All costs for the month by item and site'], ['py-cost-site', 'Cost by Site', 'Cost per project + chart'],
    ['py-grade-cost', 'Grade Cost Summary', 'Cost per salary grade'], ['py-emp-financial', 'Employee Financial', 'Each person: package, benefits, cost to company'],
    ['py-entitlements', 'Entitlements', 'Every employee × every benefit'], ['py-benefits-due', 'Benefits Due', 'Names due a benefit this month'],
    ['py-benefits-statement', 'Benefits Statement', 'Printable list of benefits to pay this month'], ['py-emp-statement', 'Employee Statement', 'One employee: salary, benefits, total reward, balances'],
    ['py-paye', 'PAYE Schedule', 'PAYE to remit'], ['py-pension', 'Pension Schedule', 'Pension to remit by PFA'], ['py-payslips', 'Payslips', 'Printable payslip for any employee'],
    ['py-engine', 'Payroll Engine', 'All calculations'], ['payroll-engine', 'Payroll Runs & Approval', 'Save, approve and pay the month']]],
]
const ROUTINE = ['Date Control: pick the payroll month and year.', "Attendance Import: clear the old paste, paste this month's attendance report (with headings). Fix anything flagged in the Check column; corrections go on Attendance Manual.",
  "Monthly Deductions and Other Expenses: enter this month's leave days, advances, losses and additions.", 'New loans → Loan Schedule. New staff → Employees.',
  'Check the Checks box is all 0 / OK.', 'Review Master Summary and Financial; print payslips. Pay separate benefits from Benefits Statement (Show: Separately).',
  'Save the month in Payroll Runs & Approval and approve it.']

export default function Navigator() {
  const [meta, , metaErr] = usePyMeta()
  const [period] = usePyPeriod(meta)
  const [d, err, reload] = usePyData(period)
  const [all, setAll] = useState(false)
  const view = useMemo(() => {
    if (!d) return null
    const paid = d.rows.filter(r => r.paid)
    const T = d.totals
    const issues = d.rows.filter(r => r.checks.length)
    const bySite = d.projects.map(p => ({ name: p.name, cost: paid.reduce((t, r) => t + r.cost * (r.alloc[p.name] || 0), 0) })).filter(x => x.cost > 0)
      .sort((a, b) => b.cost - a.cost)
    const structs = Object.entries(d.by_structure).filter(([, x]) => x.staff).map(([k, x]) => ({ name: k, ...x }))
    const benDue = d.benefits.reduce((t, b) => t + b.due_all, 0)
    const benSep = d.benefits.filter(b => b.paid_via === 'SEPARATELY').reduce((t, b) => t + b.due_all, 0)
    return { paid, T, issues, bySite, structs, benDue, benSep }
  }, [d])
  if (err || metaErr) return <ErrorBox error={err || metaErr} />
  if (!d || !view) return <Loading what="the payroll navigator" />
  const { T, issues, bySite, structs, benDue, benSep } = view
  const c = d.checks
  const checks = [
    ['Staff with an issue (see list below)', c.issues], ['Staff on a grade not in Salary Grades', c.grade_missing],
    ['Salary components add to 100%', c.components_ok ? 'OK' : 'CHECK'], ['Attendance with hours but no project', c.hours_no_project],
    ['Staff with the same day on 2+ site sheets', c.multi_site], ['Loans with an unknown employee', c.loans_unknown],
    ['Attendance lines needing attention (import)', c.att_attention], ['Financial reconciles to payroll', c.reconcile ? 'OK' : 'CHECK'],
  ]
  const P = d.period
  return (
    <div className="ps py">
      <PyHeader title={`${meta?.company || 'RIFITEL'} PAYROLL`} sub={`Payroll month: ${P.label.toUpperCase()}  ·  Period ${P.text}  ·  ${P.sched_wd} working days`}>
        <Link className="mdr-btn" to={to('py-date-control')}><CalendarCog size={14} /> Change Month</Link>
        <button className="mdr-btn" onClick={reload}><RefreshCw size={14} /> Refresh</button>
        <Link className="mds-run" to={to('payroll-engine')}><CheckCircle2 size={14} /> Payroll Runs & Approval</Link>
      </PyHeader>

      <div className="ps-kpis py-kpis">
        <Kpi label="Staff Paid" value={n0(T.paid)} Ico={Users} note={structs.map(s => `${s.name} ${s.staff}`).join(' · ') || 'No paid staff'} />
        <Kpi label="Gross Earnings" value={naira(T.gross)} Ico={CircleDollarSign} tone="blue" i={1} />
        <Kpi label="Total Deductions" value={naira(T.total_ded)} Ico={MinusCircle} tone="red" i={2} />
        <Kpi label="Net Pay to Staff" value={naira(T.net)} Ico={Banknote} tone="green" i={3} />
        <Kpi label="PAYE to Remit" value={naira(T.paye_calc)} Ico={Landmark} tone="amber" i={4} />
        <Kpi label="Pension to Remit (EE+ER)" value={naira(T.pension_ee_calc + T.pension_er)} Ico={ShieldCheck} tone="purple" i={5} />
        <Kpi label="Benefits Due This Month" value={naira(benDue)} Ico={Gift} tone="teal" note={`of which paid separately ${naira(benSep)}`} i={6} />
        <Kpi label="Total Staff Cost" value={naira(T.cost)} Ico={Wallet} tone="navy" note="Gross + employer statutory + benefits outside salary + other expenses" i={7} />
      </div>

      <div className="hre-grid2">
        <Panel title="Checks" note="should all be 0 / OK">
          <ul className="py-checks">
            {checks.map(([l, v]) => {
              const ok = v === 0 || v === 'OK'
              return <li key={l} className={ok ? 'ok' : 'bad'}>{ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}<span>{l}</span><b>{v}</b></li>
            })}
          </ul>
          <div className="py-run">
            Payroll run for {P.label}: {d.run ? <Chip tone={{ draft: 'gray', calculated: 'blue', approved: 'green', paid: 'teal' }[d.run.status]}>{d.run.status}</Chip> : <Chip tone="amber">not saved</Chip>}
            {d.run && <span> · net {naira(d.run.net)}</span>}
          </div>
        </Panel>
        <Panel title="Staff With Issues" note={`${issues.length} staff`}>
          {issues.length ? <>
            <ul className="py-issues">
              {(all ? issues : issues.slice(0, 15)).map(r => <li key={r.id}><b>{r.employee_no} — {r.name}</b><span>{r.checks.join(' ')}</span></li>)}
            </ul>
            {issues.length > 15 && <button className="ps-more py-link" onClick={() => setAll(!all)}>{all ? 'Show first 15' : `Show all ${issues.length}`}</button>}
          </> : <div className="hre-empty">No issues — every employee is clean.</div>}
        </Panel>
        <Panel title="Cost by Pay Structure" note="gross · net · cost to company">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={structs} margin={{ left: 8, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={nairaK} width={64} />
              <Tooltip formatter={(v, n) => [naira(v), n]} />
              <Bar dataKey="gross" name="Gross" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net" name="Net" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Cost to company" fill="#0b318b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Staff Cost by Site" note={`${bySite.length} site(s)`}>
          {bySite.length ? <div className="md-pie hre-pie">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={bySite} dataKey="cost" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={1} stroke="#fff">
                {bySite.map((x, i) => <Cell key={x.name} fill={PALETTE[i % PALETTE.length]} />)}</Pie>
                <Tooltip formatter={v => naira(v)} /></PieChart>
            </ResponsiveContainer>
            <ul className="md-legend">{bySite.slice(0, 8).map((x, i) => <li key={x.name}><i style={{ background: PALETTE[i % PALETTE.length] }} /><span>{x.name}</span><b>{nairaK(x.cost)}</b></li>)}</ul>
          </div> : <div className="hre-empty">No cost allocated to sites.</div>}
        </Panel>
      </div>

      <div className="py-nav">
        {NAV.map(([title, items]) => (
          <div key={title} className="mdr-card py-nav-sec">
            <h3>{title}</h3>
            <div className="py-nav-grid">
              {items.map(([k, l, desc]) => <Link key={k} to={to(k)} className="py-nav-item"><b>{l}</b><small>{desc}</small></Link>)}
            </div>
          </div>
        ))}
        <div className="mdr-card py-nav-sec">
          <h3><ListChecks size={16} /> Every month</h3>
          <ol className="py-routine">{ROUTINE.map(x => <li key={x}>{x}</li>)}</ol>
        </div>
      </div>
    </div>
  )
}
